import React, { useMemo, useState } from "react";

function SuspiciousBiddingPage({ users, dateFilteredAuctions }) {
  const [searchText, setSearchText] = useState("");

  const formatKWD = (value) => {
    return `${Number(value || 0).toFixed(3)} KWD`;
  };

  const getTime = (value) => {
    if (!value) return 0;
    if (value?.seconds) return value.seconds * 1000;
    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);
    if (!time) return "—";
    return new Date(time).toLocaleString();
  };

  const suspiciousRows = useMemo(() => {
    return dateFilteredAuctions
      .map((auction) => {
        const bidHistory = Array.isArray(auction.bidHistory)
          ? auction.bidHistory
          : Array.isArray(auction.bids)
          ? auction.bids
          : [];

          const totalBidSignals = bidHistory.length;

        const bidderNames = bidHistory
          .map(
            (bid) =>
              bid.username ||
              bid.bidderUsername ||
              bid.bidder ||
              bid.user ||
              bid.email ||
              bid.uid ||
              ""
          )
          .filter(Boolean);

        const uniqueBidders = new Set(bidderNames).size;

        const bidderCounts = {};

        bidderNames.forEach((bidder) => {
          bidderCounts[bidder] = Number(bidderCounts[bidder] || 0) + 1;
        });

        const repeatedBidderEntry =
          Object.entries(bidderCounts).length > 0
            ? Object.entries(bidderCounts).sort((a, b) => b[1] - a[1])[0]
            : null;

            const repeatedBidderCount = repeatedBidderEntry
            ? repeatedBidderEntry[1]
            : 0;
  
          const repeatedBiddersList = Object.entries(bidderCounts)
            .filter(([, count]) => Number(count || 0) >= 2)
            .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
            .map(([bidder, count]) => `${bidder} (${count})`);

            const currentBid = Number(auction.currentBid || 0);
            const winnerName = auction.currentWinner || auction.winner || "";
            const sellerName = auction.createdByUsername || auction.createdBy || "";
            const sellerWinnerPairKey =
              sellerName && winnerName ? `${sellerName} → ${winnerName}` : "—";
    
            let riskScore = 0;
            const reasons = [];

        if (totalBidSignals >= 10 && uniqueBidders <= 2) {
          riskScore += 30;
          reasons.push("High bid activity with low unique bidders");
        }

        if (repeatedBidderCount >= 8) {
          riskScore += 25;
          reasons.push("Same bidder appears many times");
        }

        if (currentBid >= 50 && uniqueBidders <= 1) {
          riskScore += 20;
          reasons.push("High current bid with very low bidder diversity");
        }

        if (auction.flagged === true) {
          riskScore += 20;
          reasons.push("Auction is flagged");
        }

        if (Number(auction.reportCount || 0) > 0 || auction.reported === true) {
          riskScore += 15;
          reasons.push("Auction has reports");
        }

        if (auction.status === "removed") {
          riskScore += 25;
          reasons.push("Auction was removed");
        }

        if (
          winnerName &&
          sellerName &&
          winnerName === repeatedBidderEntry?.[0] &&
          repeatedBidderCount >= 3
        ) {
          riskScore += 15;
          reasons.push("Winner is also repeated bidder");
        }

        if (
          auction.closureReason === "winner_declared" &&
          uniqueBidders <= 1 &&
          currentBid > 0
        ) {
          riskScore += 20;
          reasons.push("Winner declared with very low bidder diversity");
        }

        let riskLevel = "Low";

        if (riskScore >= 70) {
          riskLevel = "High";
        } else if (riskScore >= 35) {
          riskLevel = "Medium";
        }

        return {
          auctionId: auction.id,
          title: auction.title || "Untitled Auction",
          seller: auction.createdByUsername || auction.createdBy || "—",
          sellerEmail: auction.createdBy || "—",
          status: auction.status || "unknown",
          currentBid,
          totalBidSignals,
          uniqueBidders,
          repeatedBidder:
          repeatedBidderEntry?.[0] || auction.currentWinner || auction.winner || "—",
        repeatedBidderCount,
        repeatedBiddersList,
        winnerName: winnerName || "—",
        sellerWinnerPairKey,
        reports: Number(auction.reportCount || 0),
          flagged: auction.flagged === true,
          createdAt: auction.createdAt,
          riskScore,
          riskLevel,
          reasons,
        };
      })
      .filter(
        (row) =>
          row.riskScore > 0 &&
          (Number(row.currentBid || 0) > 0 ||
            Number(row.totalBidSignals || 0) > 0)
      )
      .sort((a, b) => Number(b.riskScore || 0) - Number(a.riskScore || 0));
  }, [dateFilteredAuctions]);

  const filteredRows = suspiciousRows.filter((row) => {
    const search = searchText.toLowerCase().trim();

    if (!search) return true;

    return (
      String(row.title || "").toLowerCase().includes(search) ||
      String(row.seller || "").toLowerCase().includes(search) ||
      String(row.sellerEmail || "").toLowerCase().includes(search) ||
      String(row.riskLevel || "").toLowerCase().includes(search)
    );
  });

  const highRiskCount = suspiciousRows.filter(
    (row) => row.riskLevel === "High"
  ).length;

  const mediumRiskCount = suspiciousRows.filter(
    (row) => row.riskLevel === "Medium"
  ).length;

  const lowRiskCount = suspiciousRows.filter(
    (row) => row.riskLevel === "Low"
  ).length;

  const getRiskBadgeClass = (riskLevel) => {
    if (riskLevel === "High") return "badge failed";
    if (riskLevel === "Medium") return "badge pending";
    return "badge reviewed";
  };

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Suspicious Auctions</span>
          <strong>{suspiciousRows.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on bidding and auction risk signals
          </small>
        </div>

        <div className="stat-card">
          <span>High Risk Bidding</span>
          <strong>{highRiskCount}</strong>
        </div>

        <div className="stat-card">
          <span>Medium Risk Bidding</span>
          <strong>{mediumRiskCount}</strong>
        </div>

        <div className="stat-card">
          <span>Low Risk Bidding</span>
          <strong>{lowRiskCount}</strong>
        </div>

        <div className="stat-card">
          <span>Bid History Status</span>
          <strong>Active for New Bids</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Older auctions may show 0 because bid history was added later
          </small>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Suspicious Bidding</h2>
            <p>
              Auction-level bidding risk signals. This is a review tool only,
              not automatic proof of fraud.
            </p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search auction, seller, email, or risk level..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        {filteredRows.length === 0 ? (
          <p className="empty-text">No suspicious bidding signals found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Risk Level</th>
                  <th>Auction ID</th>
                  <th>Auction</th>
                  <th>Seller</th>
                  <th>Seller Email</th>
                  <th>Status</th>
                  <th>Current Bid</th>
                  <th>Stored Bid Records</th>
                  <th>Unique Bidders</th>
                  <th>Repeated Bidders</th>
                  <th>Repeated Count</th>
                  <th>Winner</th>
                  <th>Seller → Winner Pair</th>
                  <th>Reports</th>
                  <th>Flagged</th>
                  <th>Risk Score</th>
                  <th>Detected Reasons</th>
                  <th>Created At</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.auctionId}>
                    <td>
                      <span className={getRiskBadgeClass(row.riskLevel)}>
                        {row.riskLevel}
                      </span>
                    </td>
                    <td>{row.auctionId}</td>
                    <td>{row.title}</td>
                    <td>{row.seller}</td>
                    <td>{row.sellerEmail}</td>
                    <td>
                      <span className={`badge ${row.status}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{formatKWD(row.currentBid)}</td>
                    <td>{Number(row.totalBidSignals || 0)}</td>
                    <td>{Number(row.uniqueBidders || 0)}</td>
                    <td>
                    {row.repeatedBiddersList?.length
                    ? row.repeatedBiddersList.join(" | ")
                    : row.repeatedBidder}
                    </td>
                    <td>{Number(row.repeatedBidderCount || 0)}</td>
                    <td>{row.winnerName || "—"}</td>
                    <td className="small-muted">{row.sellerWinnerPairKey || "—"}</td>
                    <td>{Number(row.reports || 0)}</td>
                    <td>{row.flagged ? "Yes" : "No"}</td>
                    <td>{Number(row.riskScore || 0)}</td>
                    <td className="small-muted">
                      {row.reasons.length > 0
                        ? row.reasons.join(" | ")
                        : "Minor signal only"}
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default SuspiciousBiddingPage;